import { PayoutAmount } from '@evi/ui';
import { formatReportMoney,hostShareCopy } from '@evi/shared';
import { useState, useEffect, useCallback } from 'react';
import { Receipt, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { fetchPayouts, payoutsCsvQuery, formatCents, type Payout } from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { heldElectricityCents, heldNetCents, isHeldPayout } from '@/lib/heldPayout';
import styles from './PayoutsPage.module.css';
import { CanadianPayoutNote } from '@/components/payouts/CanadianPayoutNote';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  paid: 'Paid',
  payment_pending: 'Payment Pending',
  payment_failed: 'Payment Failed',
  transferred: 'Transferred',
  failed: 'Transfer Failed',
  refunded: 'Refunded',
};

export function PayoutsPage() {
  useRecentRoute('Payouts');
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchPayouts({ page, limit: 25, status: statusFilter || undefined });
      setPayouts(res.data);
      setTotalPages(res.meta.pages);
      setTotal(res.meta.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // CSV download through the authenticated ky client. The old
  // <a href={exportPayoutsCsvUrl()} download> 401'd: a top-level
  // navigation carries cookies but not the JS-held Bearer token the auth
  // plugin reads. Same request as before (billing/payouts?format=csv, no
  // filters); the server's Content-Disposition names the file.
  const [csvBusy, setCsvBusy] = useState(false);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: 'billing/payouts',
        query: payoutsCsvQuery(),
        fallbackFilename: `payouts-${today}.csv`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export payouts');
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy]);

  return (
    <div className={styles.page}>
      <CanadianPayoutNote />
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Receipt size={18} /></div>
          <div>
            <h1 className={styles.title}>Payouts</h1>
            <p className={styles.subtitle}>{total} payout record{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          type="button"
          className={styles.csvBtn}
          onClick={handleDownloadCsv}
          disabled={csvBusy}
        >
          {csvBusy
            ? <Loader2 size={14} aria-hidden />
            : <Download size={14} />} Export CSV
        </button>
      </div>

      <div className={styles.filterRow}>
        <select className={styles.select} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading...</p>
      ) : (
        <PayoutsResponsiveTable payouts={payouts} />
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

/** EVI's percentage part of a payout: the platform fee without the
 *  connection fee and before any EVI-funded discount. Mirrors
 *  platformPercentPartCents in @evi/billing, which the admin app does
 *  not depend on. Pre-Tier-2 rows lack both fields and read as the old
 *  platform fee. */
function percentPartCents(b: Payout['breakdown']): number {
  return Math.max(
    0,
    b.platform_fee_cents + (b.evi_funded_discount_cents ?? 0) - (b.connection_fee_cents ?? 0),
  );
}

/** Collect and hold: settlement refused the split because the Site's
 *  electricity reimbursement exceeded the session's energy revenue. The
 *  driver paid in full and the breakdown carries the whole charge as
 *  EVI's platform fee, 0 to the host and Site, until a human pays them.
 *  Read as an ordinary row it says "EVI took everything", so every cell
 *  that would say that says "held" instead (@/lib/heldPayout, shared with
 *  the Reports ledger). */
const isHeld = isHeldPayout;

/** "Site electricity $4.10 exceeded energy revenue $3.20": the numbers the
 *  refused split was computed from, for the human who pays it out. */
function heldDetail(p: Payout): string {
  const cur = p.breakdown.currency;
  const elec = p.held_electricity_cost_cents;
  const energy = p.held_energy_revenue_cents;
  if (typeof elec !== 'number' || typeof energy !== 'number') {
    return 'Site electricity exceeded what the driver paid for charging';
  }
  return `Site electricity ${formatCents(elec, cur)} exceeded energy revenue ${formatCents(energy, cur)}`;
}

/** "$3.58 held on EVI's balance: the $4.00 charge less Stripe's $0.42 fee".
 *  What EVI actually holds is breakdown.net_revenue_cents: platform_fee_cents
 *  on a held row is the WHOLE charge, gross, while Stripe's fee comes off as
 *  usual, so the gross overstates the balance by that fee. */
function heldBalanceDetail(p: Payout): string {
  const cur = p.breakdown.currency;
  return `${formatCents(heldNetCents(p) ?? 0, cur)} held on EVI's balance: the `
    + `${formatCents(p.breakdown.gross_amount_cents, cur)} charge less Stripe's `
    + `${formatCents(p.breakdown.stripe_fee_cents, cur)} fee`;
}

function PayoutsResponsiveTable({ payouts }: { payouts: Payout[] }) {
  const columns: Column<Payout>[] = [
    {
      key: 'date',
      header: 'Date',
      mobile: 'primary',
      cell: (p) => (
        <span className={styles.dimText}>
          {new Date(p.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'secondary',
      cell: (p) => (
        <>
          <span className={`${styles.statusBadge} ${styles[`status_${p.status}`] ?? ''}`}>
            {STATUS_LABELS[p.status] ?? p.status}
          </span>
          {isHeld(p) && (
            <>
              <span
                className={styles.heldBadge}
                title={`${heldDetail(p)}. The driver paid in full; ${heldBalanceDetail(p)}, until the host and Site are paid manually.`}
              >
                Held for manual payout
              </span>
              <span className={styles.subNote}>{heldDetail(p)}</span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'energy',
      header: 'Energy',
      mobile: 'body',
      align: 'right',
      cell: (p) => `${p.breakdown.energy_kwh.toFixed(2)} kWh`,
    },
    {
      key: 'gross',
      header: 'Gross',
      mobile: 'body',
      align: 'right',
      cell: (p) => <span className={styles.amount}>{formatCents(p.breakdown.gross_amount_cents,p.breakdown.currency)}
        {!!p.breakdown.site_fee_cents&&<span className={styles.subNote}>{hostShareCopy(navigator.language).fee} {formatCents(p.breakdown.site_fee_cents,p.breakdown.currency)} ·
          {hostShareCopy(navigator.language).site} {formatCents(p.breakdown.site_share_cents??0,p.breakdown.currency)}<br/>
          {hostShareCopy(navigator.language).description}</span>}</span>,
    },
    // Breakdown deductions live off-screen in the mobile card body to
    // keep the primary number (owner payout) uncluttered at phone
    // size. Hidden on mobile.
    {
      key: 'idle-subtotal',
      header: 'Idle subtotal', mobile: 'body',
      cell: (p) => <strong>{formatReportMoney(p.idle_subtotal_cents, p.breakdown.currency)}</strong>,
    },
    {
      key: 'payment-legs', header: 'Recorded payment legs', mobile: 'body',
      cell: (p) => p.payment_legs?.length ? <div>{p.payment_legs.map(leg => <div key={leg.id}>
        <span>{leg.kind}</span><br /><code style={{ overflowWrap: 'anywhere', fontSize: 11 }}>{leg.id}</code>
      </div>)}</div> : 'Not recorded',
    },
    {
      key: 'stripeFee',
      header: 'Stripe Fee',
      mobile: 'hidden',
      align: 'right',
      cell: (p) => (
        <span className={styles.deduction}>
          −{formatCents(p.breakdown.stripe_fee_cents, p.breakdown.currency)}
        </span>
      ),
    },
    {
      key: 'electricity',
      header: 'Electricity',
      mobile: 'hidden',
      align: 'right',
      cell: (p) => {
        // A held row's breakdown carries 0 electricity: the Site is owed
        // its cost, paid by hand, not reimbursed nothing. Show the owed
        // amount the refused split was computed from.
        const heldElectricity = heldElectricityCents(p);
        if (heldElectricity !== null) {
          return (
            <span
              className={styles.deduction}
              title="The Site's electricity cost, held with the charge and paid manually."
            >
              held {formatCents(heldElectricity, p.breakdown.currency)}
            </span>
          );
        }
        return (
          <span className={styles.deduction}>
            −{formatCents(p.breakdown.electricity_cost_cents, p.breakdown.currency)}
          </span>
        );
      },
    },
    // Platform Fee is EVI's WHOLE take and includes the connection fee
    // (Tier 2), so a $1-fee session reads −$1.12 here, not −$0.12. The
    // next column shows the fee part so that jump is explained; it is
    // not a second deduction. An EVI-funded discount comes off EVI's
    // percentage part only, so it is noted under this cell rather than
    // given a column: the host's legs never see it.
    {
      key: 'platformFee',
      header: 'Platform Fee',
      mobile: 'hidden',
      align: 'right',
      cell: (p) => {
        const cur = p.breakdown.currency;
        // A held row's platform fee is the whole charge, parked, not EVI's
        // take: the "15% share" arithmetic below would be meaningless on it.
        // What sits on EVI's balance is that charge less Stripe's fee
        // (net_revenue_cents), so the cell shows that, not the gross the
        // breakdown records: Gross − Stripe fee − held reconciles to the
        // $0.00 owner payout.
        if (isHeld(p)) {
          return (
            <span
              className={styles.deduction}
              title={`${heldBalanceDetail(p)}. Not EVI's fee. ${heldDetail(p)}.`}
            >
              held {formatCents(heldNetCents(p) ?? 0, cur)}
              <span className={styles.subNote}>on EVI&apos;s balance, not EVI&apos;s fee</span>
            </span>
          );
        }
        const fee = p.breakdown.connection_fee_cents ?? 0;
        const discount = p.breakdown.evi_funded_discount_cents ?? 0;
        let title = `${p.breakdown.platform_percentage}% share ${formatCents(percentPartCents(p.breakdown), cur)}`;
        if (fee > 0) title = `Connection fee ${formatCents(fee, cur)} + ${title}`;
        if (discount > 0) title += ` − EVI-funded discount ${formatCents(discount, cur)}`;
        return (
          <span className={styles.deduction} title={title}>
            −{formatCents(p.breakdown.platform_fee_cents, cur)}
            {discount > 0 && (
              <span className={styles.subNote}>
                after {formatCents(discount, cur)} EVI discount
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'connectionFee',
      header: (
        <span title="EVI's connection fee, kept by EVI whole. Included in Platform Fee.">
          Connection fee (EVI)
        </span>
      ),
      mobile: 'hidden',
      align: 'right',
      cell: (p) => (
        <span className={styles.deduction}>
          {formatCents(p.breakdown.connection_fee_cents ?? 0, p.breakdown.currency)}
        </span>
      ),
    },
    {
      key: 'ownerPayout',
      header: 'Owner Payout',
      mobile: 'body',
      align: 'right',
      cell: (p) => (
        <span className={`${styles.amount} ${styles.payoutAmount}`}>
          <PayoutAmount cents={p.breakdown.owner_payout_cents} currency={p.breakdown.currency} cash={p.host_cash_by_currency} locale={navigator.language} />
          {isHeld(p) && <span className={styles.subNote}>held, paid manually</span>}
        </span>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={payouts}
      rowKey={(p) => p._id}
      emptyMessage="No payouts yet — sessions need to complete first"
    />
  );
}
